/**
 * Cart Drawer Upsell Component
 *
 * Handles add-to-cart for upsell products inside the cart drawer.
 * Dispatches a CartAddEvent-compatible CustomEvent so cart-items-component
 * picks up the change and re-renders the drawer via the section rendering API.
 */


import { announceCartLinesUpdate } from '@theme/cart-events';
class CartDrawerUpsell extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick.bind(this));
  }

  /**
   * @param {MouseEvent} event
   */
  async #handleClick(event) {
    // Handle Quick Add button → open overlay
    const quickAddBtn = event.target.closest('[data-open-quick-add]');

    if (quickAddBtn && !quickAddBtn.disabled) {
      this.#openQuickAdd(quickAddBtn);
      return;
    }

    const button = event.target.closest('[data-variant-id]');

    if (!button || button.disabled) {
      return;
    }

    const variantId = Number(button.dataset.variantId);

    if (!variantId) {
      return;
    }

    button.classList.add('is-loading');
    const originalText = button.textContent;
    button.textContent = '...';
    button.disabled = true;

    try {
      const response = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          items: [{ id: variantId, quantity: 1 }],
          sections: this.#getSectionIds(),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.description || 'Failed to add item');
      }

      const data = await response.json();

      await announceCartLinesUpdate({
        action: 'add',
        lines: [{ merchandiseId: String(variantId), quantity: 1 }],
        source: 'cart-drawer-upsell',
        sourceId: 'cart-drawer-upsell',
        sections: data.sections,
        // `/cart/add.js` returns the added line items with their merged quantity — enough for
        // `detail.items`, so the announcement can resolve without a second round trip.
        items: data.items,
        variantId: String(variantId),
      });

      button.textContent = 'Added!';
      setTimeout(() => {
        button.textContent = originalText;
      }, 1200);
    } catch (error) {
      console.error('[cart-drawer-upsell]', error.message);
      button.textContent = 'Error';
      setTimeout(() => {
        button.textContent = originalText;
        button.disabled = false;
      }, 1500);
    } finally {
      button.classList.remove('is-loading');
    }
  }

  /**
   * Open the Quick Add overlay for a multi-variant product.
   * Reads product JSON from the sibling <script> tag.
   * @param {HTMLButtonElement} button
   */
  #openQuickAdd(button) {
    const productId = button.dataset.openQuickAdd;
    const jsonScript = this.querySelector(`script[data-product-json="${productId}"]`);

    if (!jsonScript) {
      return;
    }

    try {
      const productData = JSON.parse(jsonScript.textContent);
      const quickAddEl = document.querySelector('cart-drawer-quick-add');

      if (quickAddEl && typeof quickAddEl.open === 'function') {
        quickAddEl.open(productData);
      }
    } catch (error) {
      console.error('[cart-drawer-upsell] Failed to parse product JSON', error);
    }
  }

  /**
   * Get section IDs for section rendering API.
   * Cart items component listens for sections to update the drawer.
   * @returns {string}
   */
  #getSectionIds() {
    const cartItemsComponent = this.closest('cart-items-component');

    if (cartItemsComponent) {
      const sectionId = cartItemsComponent.dataset.sectionId;

      if (sectionId) {
        return sectionId;
      }
    }

    return '';
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}
